import { useLayoutEffect, useRef, useMemo, useEffect, forwardRef } from "react";
// eslint-disable-next-line no-undef
const { WindowVirtualizer, Virtualizer } = require("virtua");

// --- SessionStorage Availability Check ---
function isSessionStorageAvailable() {
  try {
    if (typeof window === "undefined" || typeof window.sessionStorage === "undefined") {
      return false;
    }
    const testKey = "__restorable_list_test__";
    window.sessionStorage.setItem(testKey, testKey);
    window.sessionStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

// virtua hides this element and opts each measured row back in with `visibility: visible`.
// WebKit will not request an <img> anywhere under a `visibility: hidden` ancestor, so Safari
// leaves the product shimmer up and never contacts the image host. Rows that are still
// unmeasured keep their own `visibility: hidden`, so showing the container does not flash them.
const ListSurface = forwardRef(function ListSurface({ style, ...props }, ref) {
  return <div ref={ref} {...props} style={{ ...style, visibility: "visible" }} />;
});

const RestorableList = ({ children, useVirtualizer = false }) => {
  const cacheKey = "window-list-cache-1";
  const ref = useRef(null);

  // Check if sessionStorage is available
  const isSessionStorageAvailable = () => {
    try {
      const testKey = "__storage_test__";
      window.sessionStorage.setItem(testKey, testKey);
      window.sessionStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  };

  // Safe sessionStorage getter
  const safeGetItem = (key) => {
    try {
      if (isSessionStorageAvailable()) {
        return window?.sessionStorage?.getItem(key);
      }
      return null;
    } catch {
      return null;
    }
  };

  // Safe sessionStorage setter
  const safeSetItem = (key, value) => {
    try {
      if (isSessionStorageAvailable()) {
        window?.sessionStorage?.setItem(key, value);
      }
    } catch {
      // private mode / denied — silent
    }
  };

  // Safe sessionStorage remover
  const safeRemoveItem = (key) => {
    try {
      if (isSessionStorageAvailable()) {
        window?.sessionStorage?.removeItem(key);
      }
    } catch {
      // private mode / denied — silent
    }
  };

  // Add error handling for ResizeObserver
  useEffect(() => {
    const handleError = (error) => {
      if (error.message.includes("ResizeObserver")) {
        error.stopPropagation();
        return false;
      }
    };
    window.addEventListener("error", handleError);
    return () => window.removeEventListener("error", handleError);
  }, []);

  const [offset, cache] = useMemo(() => {
    let serialized;

    serialized = safeGetItem(cacheKey);

    if (!serialized) return [];
    try {
      return JSON.parse(serialized);
      // eslint-disable-next-line no-unused-vars
    } catch (e) {
      return [];
    }
  }, []);

  useLayoutEffect(() => {
    if (!ref.current) return;
    const handle = ref.current;
    window.scrollTo(0, offset ?? 0);
    safeRemoveItem(cacheKey);
    let scrollY = 0;
    const onScroll = () => {
      scrollY = window.scrollY;
    };
    window.addEventListener("beforeunload", () => {
      safeRemoveItem(cacheKey);
    });
    window.addEventListener("scroll", onScroll);
    onScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
      // Use stored window.scrollY because it may return 0 in useEffect cleanup
      safeSetItem(cacheKey, JSON.stringify([scrollY, handle.cache]));
    };
  }, [offset]);

  if (useVirtualizer) {
    return <Virtualizer as={ListSurface}>{children}</Virtualizer>;
  }

  return (
    <WindowVirtualizer ref={ref} as={ListSurface} cache={cache}>
      {children}
    </WindowVirtualizer>
  );
};

export default RestorableList;
